import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';

import useSessionStorage from '../hooks/useSessionStorage';
import { Organization, OrganizationAccountType, useMeQuery, User } from '../types/api.graphql';
import { alphabetizeComparer } from '../utils/sort';
import { ALL_DEALERSHIPS_KEY } from '../views/constants';
import { useAuthContext } from './AuthContext';

type Nullable<T> = T | null;

interface SessionContextProps {
  me: Nullable<User>;
  hasDealWithPaidSubscription: boolean;
  hasComplianceDealership: boolean;
  activeDealerships: ReadonlyArray<Organization>;
  previousHelpNavigationPath: string | null;
  setPreviousPath: (path: string) => void;
  currentSelectedDealershipKey: string | null;
  setCurrentSelectedDealershipKey: (key: string) => void;
}

interface SessionProviderProps {
  children: ReactNode;
}

const SessionContext = createContext<SessionContextProps>({
  me: null,
  hasDealWithPaidSubscription: false,
  hasComplianceDealership: false,
  activeDealerships: [],
  previousHelpNavigationPath: null,
  setPreviousPath: () => {
    throw new Error('setPreviousTabItem must be used within SessionProvider');
  },
  currentSelectedDealershipKey: ALL_DEALERSHIPS_KEY,
  setCurrentSelectedDealershipKey: () => {
    throw new Error('setCurrentSelectedDealership must be used within SessionProvider');
  },
});

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const { authToken } = useAuthContext();
  const { data } = useMeQuery({ skip: !authToken });
  const [previousHelpNavigationPath, setPreviousHelpNavigationPath] = useState<string | null>(null);
  const [currentSelectedDealershipKey, setCurrentDealerKey] = useSessionStorage<string | null>(
    'selectedDealershipKey',
    ALL_DEALERSHIPS_KEY,
  );

  const hasDealWithPaidSubscription = data?.me?.organizations?.some((org) => org?.paidSubscription);
  const activeDealerships =
    data?.me?.organizations
      ?.filter(({ cif }) => !!cif)
      ?.sort(alphabetizeComparer((item) => item.name, 'ascending', { ignorePunctuation: true })) || [];
  const hasComplianceDealership = useMemo(
    () => activeDealerships.some(({ accountType }) => accountType === OrganizationAccountType.Compliance),
    [activeDealerships],
  );

  useEffect(() => {
    if (sessionStorage.getItem('selectedDealershipKey') === null) {
      setCurrentDealerKey(ALL_DEALERSHIPS_KEY);
    }
  }, []);

  const setPreviousPath = (path: string) => setPreviousHelpNavigationPath(path);
  const setCurrentSelectedDealershipKey = (key: string) => setCurrentDealerKey(key);

  return (
    <SessionContext.Provider
      value={{
        me: data?.me || null,
        hasDealWithPaidSubscription,
        hasComplianceDealership,
        activeDealerships,
        previousHelpNavigationPath,
        setPreviousPath,
        currentSelectedDealershipKey,
        setCurrentSelectedDealershipKey,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext: () => SessionContextProps = () => {
  return React.useContext(SessionContext);
};
